import { storeToRefs } from 'pinia';

import type { GetByPartnerIdOut } from '~/types/ecommerce/generated.codegen';
import {
  getFunnelStepFromPath,
  isFunnelRoute,
  UX_JOURNEY_EVENTS,
  UX_JOURNEY_STORAGE_KEY,
  UX_PAGE_SEQUENCE_MAX,
  type FunnelStep,
  type OrderSourceResolution,
  type RecordConversionInput,
  type UxCartSnapshot,
  type UxJourneyContext,
  type UxJourneyMetrics,
  type UxJourneyState,
} from '~/types/ux-journey';

import { useDevice } from '@/composables/useDevice';

function resolveOrderSourceForConversion(
  input: RecordConversionInput,
  cartSnapshot?: UxCartSnapshot,
): OrderSourceResolution {
  if (input.order_source) {
    return {
      order_source: input.order_source,
      source_proposal_code:
        input.source_proposal_code ?? cartSnapshot?.proposal_code,
    };
  }

  if (cartSnapshot?.from_proposal) {
    return {
      order_source: 'proposal',
      ...(cartSnapshot.proposal_code != null
        ? { source_proposal_code: cartSnapshot.proposal_code }
        : {}),
    };
  }

  return { order_source: 'direct' };
}

function createJourneyId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readState(): UxJourneyState | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(UX_JOURNEY_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UxJourneyState;
  } catch {
    return null;
  }
}

function writeState(state: UxJourneyState): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(UX_JOURNEY_STORAGE_KEY, JSON.stringify(state));
}

function buildCartSnapshot(cart: GetByPartnerIdOut): UxCartSnapshot {
  const items = cart.items?.filter(Boolean) ?? [];
  const products = cart.products?.filter(Boolean) ?? [];
  const itemCount = items.length + products.length;

  const proposalCode = cart.proposal?.code ?? undefined;
  const fromProposal =
    proposalCode != null || cart.type?.description === 'proposal';

  return {
    cart_id: cart.id,
    item_count: itemCount,
    total: cart.total ?? 0,
    has_items: itemCount > 0,
    cart_type: cart.type?.description,
    category: cart.category?.description,
    ...(proposalCode != null ? { proposal_code: proposalCode } : {}),
    from_proposal: fromProposal,
  };
}

export function useUserJourney() {
  const route = useRoute();
  const userStore = useUserStore();
  const regionStore = useRegionStore();
  const { inPersonification } = storeToRefs(userStore);
  const { isMobile, isTablet, isDesktop } = useDevice();

  const getDeviceType = (): UxJourneyContext['device_type'] => {
    if (isMobile.value) return 'mobile';
    if (isTablet.value) return 'tablet';
    if (isDesktop.value) return 'desktop';
    return undefined;
  };

  const initJourney = (): UxJourneyState => {
    const existing = readState();
    if (existing) return existing;

    const now = Date.now();
    const state: UxJourneyState = {
      journey_id: createJourneyId(),
      session_started_at: now,
      milestones: {},
      last_route: {
        path: route.path,
        name: route.name?.toString() ?? null,
        entered_at: now,
      },
      page_sequence: [route.path],
      cart_visit_count: 0,
    };
    writeState(state);
    return state;
  };

  const getState = (): UxJourneyState => {
    return readState() ?? initJourney();
  };

  const persistState = (state: UxJourneyState): void => {
    writeState(state);
  };

  const getJourneyContext = (): UxJourneyContext => {
    const state = getState();
    return {
      journey_id: state.journey_id,
      partner_id: userStore.partnerId || undefined,
      is_personification: Boolean(inPersonification.value),
      region: regionStore.region || undefined,
      device_type: getDeviceType(),
      funnel_step: getFunnelStepFromPath(route.path),
      route_path: route.path,
    };
  };

  const computeMetrics = (state: UxJourneyState): UxJourneyMetrics => {
    const now = Date.now();
    const metrics: UxJourneyMetrics = {
      time_from_session_start_ms: now - state.session_started_at,
    };

    if (state.milestones.first_cart_with_items_at) {
      metrics.time_from_first_cart_ms =
        now - state.milestones.first_cart_with_items_at;
    }

    const checkoutStart =
      state.milestones.checkout_shipping_at ??
      state.milestones.checkout_payment_at;
    if (checkoutStart) {
      metrics.time_in_checkout_ms = now - checkoutStart;
    }

    return metrics;
  };

  const markFunnelMilestone = (
    state: UxJourneyState,
    step: FunnelStep,
  ): UxJourneyState => {
    const now = Date.now();
    const milestones = { ...state.milestones };

    if (step === 'cart' && state.cart_snapshot?.has_items) {
      milestones.first_cart_with_items_at ??= now;
    }
    if (step === 'shipping') {
      milestones.checkout_shipping_at ??= now;
    }
    if (step === 'payment') {
      milestones.checkout_payment_at ??= now;
    }

    return { ...state, milestones };
  };

  const handleNavigation = (
    toPath: string,
    toName?: string | null,
  ): {
    dwell_time_ms: number;
    from_path: string;
    funnel_step: FunnelStep | null;
    entered_funnel_step: FunnelStep | null;
  } => {
    const state = getState();
    const now = Date.now();
    const dwell_time_ms = now - state.last_route.entered_at;
    const from_path = state.last_route.path;
    const previousFunnelStep = getFunnelStepFromPath(from_path);
    const funnel_step = getFunnelStepFromPath(toPath);
    const entered_funnel_step =
      funnel_step && funnel_step !== previousFunnelStep ? funnel_step : null;

    let nextState: UxJourneyState = {
      ...state,
      last_route: {
        path: toPath,
        name: toName ?? null,
        entered_at: now,
      },
      page_sequence: [...state.page_sequence, toPath].slice(
        -UX_PAGE_SEQUENCE_MAX,
      ),
    };

    if (funnel_step) {
      nextState = markFunnelMilestone(nextState, funnel_step);
      if (funnel_step === 'cart') {
        nextState = {
          ...nextState,
          cart_visit_count: nextState.cart_visit_count + 1,
        };
      }
    }

    persistState(nextState);

    return {
      dwell_time_ms,
      from_path,
      funnel_step,
      entered_funnel_step,
    };
  };

  const updateCartSnapshot = (cart: GetByPartnerIdOut | undefined): void => {
    if (!cart?.id) return;

    const state = getState();
    const snapshot = buildCartSnapshot(cart);
    let nextState: UxJourneyState = { ...state, cart_snapshot: snapshot };

    if (snapshot.has_items) {
      nextState = markFunnelMilestone(nextState, 'cart');
    }

    persistState(nextState);
  };

  const recordConversion = (input: RecordConversionInput): UxJourneyMetrics => {
    const state = getState();
    const now = Date.now();
    const metrics = computeMetrics({
      ...state,
      milestones: { ...state.milestones, conversion_at: now },
    });

    const orderSourceFields =
      input.type === 'order'
        ? resolveOrderSourceForConversion(input, state.cart_snapshot)
        : {};

    const nextState: UxJourneyState = {
      ...state,
      conversion_type: input.type,
      milestones: { ...state.milestones, conversion_at: now },
    };
    persistState(nextState);

    const { trackJourney } = useJourneyTracker();
    trackJourney(
      UX_JOURNEY_EVENTS.CONVERSION_COMPLETED,
      {
        conversion_type: input.type,
        cart_id: input.cart_id,
        order_id: input.order_id,
        proposal_code: input.proposal_code,
        payment_method: input.payment_method,
        ...orderSourceFields,
        metrics,
        page_sequence: nextState.page_sequence,
      },
      { immediate: true },
    );

    return metrics;
  };

  const shouldSignalCartAbandonment = (): boolean => {
    const state = getState();
    return (
      isFunnelRoute(route.path) &&
      Boolean(state.cart_snapshot?.has_items) &&
      !state.conversion_type
    );
  };

  return {
    initJourney,
    getState,
    getJourneyContext,
    computeMetrics,
    handleNavigation,
    updateCartSnapshot,
    recordConversion,
    shouldSignalCartAbandonment,
    getFunnelStepFromPath,
    isFunnelRoute,
  };
}
